import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { AuthContext } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { ActionButton } from '../common/ActionButton';
import { JobSelector } from '../common/JobSelector';
import { Job, TimeEntry } from '../../types/backend.types';
// Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';

export const FieldWorkerDashboard: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { location, ensureFreshLocation, forceRefreshLocation, isLoading: locationLoading } = useLocation();
  const [navigating, setNavigating] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [clockedIn, setClockedIn] = useState<boolean | null>(null);
  const [activeTimeEntry, setActiveTimeEntry] = useState<any | null>(null);
  const [wrongJobWarning, setWrongJobWarning] = useState<string | null>(null);
  const [hasCheckedRedirect, setHasCheckedRedirect] = useState(false);
  const [initializing, setInitializing] = useState(true);
  // Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx
  const [initFailed, setInitFailed] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  // Ref to debounce online handler (prevents rapid re-init from network flapping)
  const lastInitTimeRef = useRef<number>(0);
  // Ref to prevent concurrent initialization calls
  const isInitializingRef = useRef<boolean>(false);

  const translations = {
    en: {
      welcome: 'Welcome',
      notClockedIn: 'NOT CLOCKED IN',
      clockedIn: 'CLOCKED IN',
      timeClock: 'Time Clock',
      jobs: 'Jobs',
      myTime: 'My Time',
      truckTickets: 'Truck Tickets',
      viewPlans: 'View Plans',
      switchJobTitle: 'Switch Job?',
      switchJobMessage: 'You are clocked into',
      switchJobMessage2: 'but appear to be at',
      switchJobQuestion: 'Would you like to switch to this job?',
      switchJob: 'Switch Job',
      cancel: 'Cancel'
    },
    es: {
      welcome: 'Bienvenido',
      notClockedIn: 'NO REGISTRADO',
      clockedIn: 'REGISTRADO',
      timeClock: 'Reloj de Tiempo',
      jobs: 'Trabajos',
      myTime: 'Mi Tiempo',
      truckTickets: 'Boletos de Camión',
      viewPlans: 'Ver Planos',
      switchJobTitle: '¿Cambiar Trabajo?',
      switchJobMessage: 'Estás registrado en',
      switchJobMessage2: 'pero parece que estás en',
      switchJobQuestion: '¿Te gustaría cambiar a este trabajo?',
      switchJob: 'Cambiar Trabajo',
      cancel: 'Cancelar'
    }
  };

  const t = translations[language];

  // Note: Job mismatch detection is handled globally by GlobalMismatchChecker in App.tsx

  // Listen for app resume to refresh location (only if stale) and re-check mismatch
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    console.log('[FieldWorkerDashboard] Setting up app resume listener for location refresh');

    const setupListeners = async () => {
      // Listen for app becoming active (resume from background)
      const resumeListener = await App.addListener('resume', async () => {
        console.log('[FieldWorkerDashboard] App resumed from background, checking if location refresh needed...');
        await ensureFreshLocation(); // Only refreshes if > 5 min old
      });

      // Also listen for appStateChange as backup
      const stateListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          console.log('[FieldWorkerDashboard] App became active, checking if location refresh needed...');
          await ensureFreshLocation(); // Only refreshes if > 5 min old
        }
      });

      return () => {
        resumeListener.remove();
        stateListener.remove();
        console.log('[FieldWorkerDashboard] Cleaned up app resume listeners');
      };
    };

    const cleanupPromise = setupListeners();

    return () => {
      cleanupPromise.then(cleanup => cleanup?.());
    };
  }, [ensureFreshLocation]);

  /**
   * Navigate to a module after ensuring fresh location (uses 5-min cache)
   * Fresh GPS is forced at critical points: job selector, clock in, clock out
   */
  const navigateWithLocationRefresh = async (path: string, options?: { state?: any }) => {
    setNavigating(true);
    try {
      await ensureFreshLocation();
      navigate(path, options);
    } finally {
      setNavigating(false);
    }
  };

  useEffect(() => {
    // Listen for language changes
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      setLanguage(newLang);
    };

    window.addEventListener('languageChange', handleLanguageChange);

    const initializeDashboard = async () => {
      // Prevent concurrent initialization calls
      if (isInitializingRef.current) {
        console.log('[FieldWorkerDashboard] Already initializing, skipping duplicate call');
        return;
      }
      isInitializingRef.current = true;

      try {
        // Track start time for minimum loading duration
        const startTime = Date.now();
        const MIN_LOADING_TIME = 800; // Minimum 800ms loading screen

        setInitFailed(false);
        setInitializing(true);

        // Load jobs and clock status in parallel
        const [jobsResponse, clockStatusResponse] = await Promise.all([
          api.get(API_CONFIG.ENDPOINTS.JOBS),
          api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`)
        ]);

        // Set jobs
        if (jobsResponse.data.success) {
          setJobs(jobsResponse.data.data);
        }

        // Check clock status
        if (clockStatusResponse.data.success && clockStatusResponse.data.data?.job_id) {
          const activeEntry = clockStatusResponse.data.data;
          setClockedIn(true);
          setActiveTimeEntry(activeEntry);

          // If clocked in, auto-select the job
          const clockedJob = jobsResponse.data.data.find((j: Job) => j.job_id === activeEntry.job_id);
          if (clockedJob) {
            setCurrentJob(clockedJob);
          }
        } else {
          // Not clocked in
          setClockedIn(false);
          setActiveTimeEntry(null);
        }

        // Ensure minimum loading time to prevent race conditions
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        // Close loading screen after minimum time and data is loaded
        setInitializing(false);
        setInitFailed(false);
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        // Mark as failed so we can retry when online
        setInitFailed(true);
        // Still enforce minimum loading time even on error
        await new Promise(resolve => setTimeout(resolve, 800));
        setInitializing(false);
      } finally {
        isInitializingRef.current = false;
      }
    };

    // Retry initialization when coming back online (with debounce to prevent rapid re-init)
    const handleOnline = () => {
      const now = Date.now();
      // Debounce: don't re-init if we initialized within the last 5 seconds
      if (now - lastInitTimeRef.current < 5000) {
        console.log('🌐 Network restored but recently initialized, skipping');
        return;
      }
      console.log('🌐 Network restored - retrying dashboard initialization');
      if (auth?.user?.employee_id) {
        lastInitTimeRef.current = now;
        initializeDashboard();
      }
    };

    window.addEventListener('online', handleOnline);

    if (auth?.user?.employee_id) {
      lastInitTimeRef.current = Date.now();
      initializeDashboard();
    }

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [auth?.user?.employee_id]);

  // Helper function to calculate distance in meters (shared)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  // Auto-redirect and location check logic
  useEffect(() => {
    // Only run this check once when all data is ready
    if (hasCheckedRedirect || clockedIn === null || jobs.length === 0) {
      return;
    }

    const checkAndRedirect = () => {

      // Check if clocked in from prior day (but allow overnight shifts before 7 AM)
      if (activeTimeEntry) {
        const clockInDate = new Date(activeTimeEntry.clock_in);
        const now = new Date();
        const isToday = clockInDate.toDateString() === now.toDateString();

        if (!isToday) {
          const currentHour = now.getHours();
          // Before 7 AM and clocked in from yesterday = likely still on shift
          // The "forgot to clock out" modal in ClockInOut handles truly stale entries
          const msInDay = 24 * 60 * 60 * 1000;
          const daysDiff = Math.floor((now.getTime() - clockInDate.getTime()) / msInDay);
          const isLikelyOvernightShift = currentHour < 7 && daysDiff <= 1;

          if (!isLikelyOvernightShift) {
            console.log('User clocked in from prior day (past 7 AM), redirecting to time clock');
            navigate('/time-clock');
            setHasCheckedRedirect(true);
            return;
          }
          console.log('Before 7 AM with yesterday clock-in - allowing dashboard (overnight shift)');
        }
      }

      // REMOVED: Auto-redirect to time clock when not clocked in at jobsite
      // Users now manually navigate to Time Clock from dashboard, which refreshes location

      // Job mismatch detection is now handled by useLocationMismatch hook
      // which properly uses actual job geofence radii instead of hardcoded 100m

      setHasCheckedRedirect(true);
    };

    checkAndRedirect();
  }, [location, clockedIn, jobs, activeTimeEntry, hasCheckedRedirect, language, navigate, calculateDistance]);

  // Auto-select clocked-in job
  useEffect(() => {
    if (!activeTimeEntry || jobs.length === 0) {
      return;
    }

    // Always auto-select the clocked-in job when user is clocked in
    const clockedInJob = jobs.find(j => j.job_id === activeTimeEntry.job_id);

    if (clockedInJob && currentJob?.job_id !== clockedInJob.job_id) {
      console.log('Auto-selecting clocked-in job:', clockedInJob.job_name);
      setCurrentJob(clockedInJob);
    }
  }, [activeTimeEntry, jobs]);

  // Note: Job switching is handled globally by GlobalMismatchChecker in App.tsx

  // Show loading screen while initializing OR navigating to a module
  // Note: Don't include locationLoading here - it can persist from other pages
  if (initializing || navigating) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          {navigating
            ? (language === 'en' ? 'Getting location...' : 'Obteniendo ubicación...')
            : (language === 'en' ? 'Loading...' : 'Cargando...')
          }
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="dashboard">

      <div className="welcome-section">
        <h2 className="welcome-text">{t.welcome}, {auth?.user?.first_name}</h2>
        {clockedIn !== null && (
          <div className={`clock-badge ${clockedIn ? 'clocked-in' : 'not-clocked-in'}`}>
            {clockedIn ? t.clockedIn : t.notClockedIn}
          </div>
        )}
      </div>

      {/* Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx */}

      <JobSelector
        jobs={jobs}
        currentJob={currentJob}
        onJobSelect={setCurrentJob}
        mode="gps"
        language={language}
      />

      <div className="actions-grid">
        <ActionButton
          label={t.timeClock}
          icon="🕐"
          onClick={() => navigateWithLocationRefresh('/time-clock')}
          gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
          disabled={navigating}
        />

        <ActionButton
          label={t.jobs}
          icon="📋"
          onClick={() => navigate('/jobs')}
          gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
        />

        <ActionButton
          label={t.myTime}
          icon="🕒"
          onClick={() => navigate('/my-time')}
          gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
        />

        <ActionButton
          label={t.truckTickets}
          icon="🚚"
          onClick={() => navigateWithLocationRefresh('/trucks')}
          gradient="linear-gradient(135deg, #667eea 0%, #5a67d8 100%)"
        />

        <ActionButton
          label={t.viewPlans}
          icon="📐"
          onClick={() => {
            // Check if a job is selected
            if (!currentJob) {
              alert('Please select a job from the dropdown above before viewing plans');
              return;
            }

            // Check if job has required information
            if (!currentJob.bradanna_job_number || currentJob.bradanna_job_number.trim() === '') {
              alert(`The job "${currentJob.job_name}" does not have a job number assigned. Please contact the office to set up the job number before viewing plans.`);
              return;
            }

            // Navigate to Plans viewer with job info
            navigate('/plans', {
              state: {
                jobName: currentJob.job_name,
                jobNumber: currentJob.bradanna_job_number
              }
            });
          }}
          gradient="linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)"
        />

      </div>
    </div>
  );
};